import { useState, useEffect, type RefObject } from 'react';

/**
 * Collapse levels for TopNavigation responsive behavior.
 * As the nav container shrinks, items collapse in priority order:
 *
 * 0 — Full layout, nothing collapsed
 * 1 — Logo + user menu → compact
 * 2 — Search bar → icon-only
 * 3 — (reserved — no change; used to hide the Notifications bell, removed in #2753)
 * 4 — (reserved — no change)
 * 5 — Engage + Analytics → "More" dropdown; Help + Order Devices hidden
 * 6 — OptiDev → "More" dropdown; Get Started pill → compact mode
 *     (icon-only progress ring, no text; ~34px instead of ~180px so the
 *     full nav stays uncollided)
 * 7 — Templates → "More" dropdown
 * 8 — Schedules → "More" dropdown
 * 9 — Playlists → "More" dropdown
 * 10 — Files/Assets → "More" dropdown (Screens stays as the anchor)
 *
 * Levels 7-10 progressively fold the primary nav items into More so they
 * never hide behind the right cluster at narrow widths — the user can
 * always find every page via the More dropdown instead of items going
 * silently unreachable.
 *
 * Note: the Get Started pill never hides — it switches between full ("Get
 * Started" text + ring) at levels 0-5 and compact (ring-only) at level 6+.
 * Losing sight of the onboarding checklist confuses users, so the icon-only
 * state preserves discoverability even at the narrowest desktop widths.
 */

const COLLAPSE_THRESHOLDS = [
  // #2753: the logo/user menu give up space first so the full search bar survives on common
  // laptop widths (1512 = 14" MacBook Pro default, 1440 = 13" MacBook Air default).
  1550, // level 1: logo + user menu → compact
  1400, // level 2: search → icon
  1350, // level 3: (reserved)
  1250, // level 4: (reserved)
  1150, // level 5: engage + analytics → more; hide help + order devices
  1050, // level 6: optidev → more; get-started pill → compact icon
  // Levels 7-10: only fold a primary nav item when keeping it would
  // truly crowd the right cluster. Each threshold sits just below the
  // viewport where that item's removal stops being necessary, so items
  // stay visible as long as there's any meaningful breathing room. Be
  // generous — items folding "a bit too soon" feels worse than a small
  // dead zone.
  770, // level 7: templates → more
  670, // level 8: schedules → more
  570, // level 9: playlists → more
  470, // level 10: files/assets → more
] as const;

export function useNavBreakpoints(ref: RefObject<HTMLElement | null>): number {
  const [collapseLevel, setCollapseLevel] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    function calcLevel(width: number): number {
      let level = 0;
      for (const threshold of COLLAPSE_THRESHOLDS) {
        if (width < threshold) {
          level++;
        } else {
          break;
        }
      }
      return level;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setCollapseLevel(calcLevel(entry.contentRect.width));
      }
    });

    observer.observe(el);
    setCollapseLevel(calcLevel(el.clientWidth));

    return () => observer.disconnect();
  }, [ref]);

  return collapseLevel;
}
